import { useMemo } from "react";

import {
  LOCALE_OPTIONS,
  LOCALE_OPTIONS_PRECISE,
  OCC_CLEARING_FEE,
  OCC_CONTRACT_CAP,
  OPTIONS_CAT_FEE_PER_EES,
  OPTIONS_MULTIPLIER,
  OPTIONS_REGULATORY_FEE,
  OPTIONS_TAF_FEE,
} from "~/v2/constants/optionOrder";
import type { CombinedLeg } from "../utils";

export interface MultiLegFeeAmounts {
  occClearingFee: number;
  optionsRegulatoryFee: number;
  tafFee: number;
  catFee: number;
  totalFees: number;
}

export interface MultiLegFeesResult {
  occClearingFee: string;
  optionsRegulatoryFee: string;
  tafFee: string;
  catFee: string;
}

export const calculateMultiLegFeeAmounts = (
  legs: CombinedLeg[],
  quantity: number,
): MultiLegFeeAmounts => {
  const safeQuantity = Number.isNaN(quantity) ? 0 : quantity;

  const { totalContracts, sellContracts } = legs.reduce(
    (acc, { form: { ratioQty, positionIntent } }) => {
      const ratio = Number.isNaN(ratioQty) ? 0 : ratioQty;
      const contracts = ratio * safeQuantity;
      return {
        totalContracts: acc.totalContracts + contracts,
        sellContracts:
          acc.sellContracts + (positionIntent.includes("sell") ? contracts : 0),
      };
    },
    { totalContracts: 0, sellContracts: 0 },
  );

  const occClearingFee =
    OCC_CLEARING_FEE * Math.min(totalContracts, OCC_CONTRACT_CAP);
  const optionsRegulatoryFee = OPTIONS_REGULATORY_FEE * totalContracts;
  const tafFee = OPTIONS_TAF_FEE * sellContracts;
  const catFee = OPTIONS_CAT_FEE_PER_EES * totalContracts * OPTIONS_MULTIPLIER;

  return {
    occClearingFee,
    optionsRegulatoryFee,
    tafFee,
    catFee,
    totalFees: occClearingFee + optionsRegulatoryFee + tafFee + catFee,
  };
};

export const formatFeeAmounts = (
  amounts: MultiLegFeeAmounts,
): MultiLegFeesResult => ({
  occClearingFee: amounts.occClearingFee.toLocaleString(
    "en-US",
    LOCALE_OPTIONS,
  ),
  optionsRegulatoryFee: amounts.optionsRegulatoryFee.toLocaleString(
    "en-US",
    LOCALE_OPTIONS,
  ),
  tafFee: amounts.tafFee.toLocaleString("en-US", LOCALE_OPTIONS),
  catFee: amounts.catFee.toLocaleString("en-US", LOCALE_OPTIONS_PRECISE),
});

export const getMultiLegFees = (
  legs: CombinedLeg[],
  quantity: number,
): MultiLegFeesResult =>
  formatFeeAmounts(calculateMultiLegFeeAmounts(legs, quantity));

export const useFees = (
  legs: CombinedLeg[],
  quantity: number,
): MultiLegFeesResult => {
  return useMemo(() => getMultiLegFees(legs, quantity), [legs, quantity]);
};
